import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getDisbursementBatchType } from '../disbursementDetails/DisbursementFoundationDetails/DisbursementFoundationDetails'

import type { DisbursementBatchCancellationFailure } from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { useFoundations } from '@percent/admin-dashboard/common/hooks'
import { AcknowledgeModal, ActionModal, CardContent, FlagAvatar, Flex, Modal, Text } from '@percent/lemonade'
import styles from './CancelDisbursementBatchModal.module.scss'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation } from '@percent/utility'
import { CancelDisbursementBatchModalProps } from './CancelDisbursementBatchModal.types'

export function CancelDisbursementBatchModal({
  isOpen,
  onClose,
  disbursementBatchId,
  endDate,
  detailedCounts,
  foundationId,
  refreshData
}: Readonly<CancelDisbursementBatchModalProps>) {
  const { t } = useTranslation()
  const { disbursementsService } = useServices()
  const { foundations } = useFoundations()
  const foundation = foundations?.[foundationId]

  const [cancelDisbursementBatchStatus, setCancelDisbursementBatchStatus] = useState<'error' | 'success' | null>(null)
  const [cancellationFailures, setCancellationFailures] = useState<DisbursementBatchCancellationFailure[] | undefined>()
  const [totalDisbursementsCount, setTotalDisbursementsCount] = useState<number | undefined>()

  const [{ isPending }, { mutateAsync: cancelDisbursementBatch }] = useMutation(
    disbursementsService.cancelDisbursementBatch
  )

  const handleClose = () => {
    if (isPending) {
      return
    }

    setCancellationFailures(undefined)
    setTotalDisbursementsCount(undefined)
    setCancelDisbursementBatchStatus(null)
    refreshData()
    onClose()
  }

  const handleSubmit = async () => {
    try {
      setCancellationFailures(undefined)
      setCancelDisbursementBatchStatus(null)

      const result = await cancelDisbursementBatch(disbursementBatchId)

      setTotalDisbursementsCount(result.data.counts.total)
      setCancellationFailures(result.data.cancellationFailures)
      setCancelDisbursementBatchStatus('success')
    } catch {
      setCancelDisbursementBatchStatus('error')
    }
  }

  const failedCount = cancellationFailures?.length ?? 0
  const hasFailures = failedCount > 0
  const succeededCount = totalDisbursementsCount === undefined ? 0 : Math.max(totalDisbursementsCount - failedCount, 0)

  const successModal = cancelDisbursementBatchStatus === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={
        hasFailures
          ? t('dialog.cancelDisbursementBatch.success.partialTitle', {
              succeededCount,
              totalDisbursementsCount
            })
          : t('dialog.cancelDisbursementBatch.success.title')
      }
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="cancel-disbursement-batch-success-modal"
      buttonTestId="close-cancel-disbursement-batch-success-modal"
    />
  )

  const errorModal = cancelDisbursementBatchStatus === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.cancelDisbursementBatch.error.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="cancel-disbursement-batch-error-modal"
      buttonTestId="close-cancel-disbursement-batch-error-modal"
    />
  )

  return (
    <Modal open={isOpen} onClose={handleClose}>
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.cancelDisbursementBatch.title')}
          primaryButtonText={t('dialog.cancelDisbursementBatch.submitButton')}
          primaryBtnTestId="cancel-disbursement-batch-modal-submit"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="cancel-disbursement-batch-modal-cancel"
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          loading={isPending}
        >
          <Flex direction="column" gap={16}>
            <Text as="p" size="small" color="gray600">
              {t('dialog.cancelDisbursementBatch.description')}
            </Text>

            <Flex direction="column" gap={8}>
              <CardContent
                className={styles.cardContent}
                title={t('typography.disbursementBatchId')}
                testId="disbursement-batch-details-id"
              >
                {disbursementBatchId}
              </CardContent>

              <CardContent
                className={styles.cardContent}
                title={t('typography.foundation')}
                testId="disbursement-batch-details-foundation"
              >
                {foundation ? (
                  <Flex align="center" gap={8}>
                    <FlagAvatar code3={foundation.countryCode} width="28px" />
                    {foundation.name}
                  </Flex>
                ) : (
                  foundationId
                )}
              </CardContent>

              <CardContent
                className={styles.cardContent}
                title={t('typography.donationsUpTo')}
                testId="disbursement-batch-details-end-date"
              >
                {dayJS(endDate).format('DD MMM YYYY')}
              </CardContent>

              <CardContent
                className={styles.cardContent}
                title={t('typography.disbursementBatchType')}
                testId="disbursement-batch-details-type"
              >
                {getDisbursementBatchType(detailedCounts)}
              </CardContent>
            </Flex>

            <Text as="p" size="small" color="gray600">
              {t('dialog.cancelDisbursementBatch.confirmation')}
            </Text>
          </Flex>
        </ActionModal>
      )}
    </Modal>
  )
}
